import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { adminApiRequest } from "../utils/adminApi";
import ProductModal from "./products/ProductModal";
import Modal from "../components/Modal";

const BASE_UPLOAD_URL = "https://kinewsstudiolimited.co.uk";

export default function ShopProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedCategory, setSelectedCategory] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "list_shop_products",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load products.");
      }

      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || "Unable to load products.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await adminApiRequest({
        script: "list_shop_categories",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load categories.");
      }

      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || "Unable to load categories.");
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [loadProducts, loadCategories]);

  const openCreateModal = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const openDeleteModal = (product) => {
    setDeleteTarget(product);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const handleDeleteSubmit = async (e) => {
    e.preventDefault();

    if (!deleteTarget) return;

    try {
      setDeleting(true);
      setError("");

      const res = await adminApiRequest({
        script: "delete_shop_product",
        data: {
          product_id: deleteTarget.id,
        },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to delete item.");
      }

      setDeleteTarget(null);
      await loadProducts();
    } catch (err) {
      setError(err.message || "Unable to delete item.");
    } finally {
      setDeleting(false);
    }
  };

  const getImage = (product) => {
    const raw = product.image_url || "";
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    return `${BASE_UPLOAD_URL}/${String(raw).replace(/^\/+/, "")}`;
  };

  const filteredProducts = useMemo(() => {
    if (selectedCategory === "all") return products;

    return products.filter(
      (p) => String(p.category_id) === String(selectedCategory)
    );
  }, [products, selectedCategory]);

  return (
    <>
      <section className="h-full min-h-0">
        <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">

          {/* HEADER */}
          <div className="flex flex-col gap-4 border-b p-5">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Shop Items</h2>

              <button
                onClick={openCreateModal}
                className="bg-slate-900 text-white px-4 py-2 rounded-xl text-sm"
              >
                Add Item
              </button>
            </div>

            {/* FILTER */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="max-w-xs border rounded-xl px-3 py-2 text-sm"
            >
              <option value="all">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* BODY (SCROLLABLE) */}
          <div className="flex-1 min-h-0 overflow-y-auto p-5">

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            )}

            {loading ? (
              <p className="text-sm text-slate-500">Loading...</p>
            ) : filteredProducts.length === 0 ? (
              <p className="text-sm text-slate-500">No products</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {filteredProducts.map((item) => {
                  const img = getImage(item);

                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 border rounded-xl p-3 hover:shadow-sm"
                    >
                      {/* IMAGE */}
                      <div className="h-16 w-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        {img ? (
                          <img
                            src={img}
                            alt={item.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full text-xs text-slate-400">
                            No image
                          </div>
                        )}
                      </div>

                      {/* INFO */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold truncate">
                          {item.name}
                        </h3>

                        <p className="text-xs text-slate-500">
                          {item.category_name || "Uncategorized"}
                        </p>

                        <p className="text-sm font-medium mt-1">
                          £{Number(item.base_price || 0).toFixed(2)}
                        </p>
                      </div>

                      {/* ACTIONS */}
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => openEditModal(item)}
                          className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => openDeleteModal(item)}
                          className="h-8 w-8 flex items-center justify-center rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MODALS */}
      <ProductModal
        isOpen={showModal}
        onClose={closeModal}
        onSaved={loadProducts}
        categories={categories}
        editingProduct={editingProduct}
      />

      {deleteTarget && (
        <Modal
          title="Delete Item"
          onClose={closeDeleteModal}
          onSubmit={handleDeleteSubmit}
          submitButtonText="Delete"
          showCancel
          showSubmitButton
          disableSubmit={deleting}
          submitButtonClass="bg-red-600 text-white"
        >
          <p className="text-sm">
            Delete <strong>{deleteTarget.name}</strong>?
          </p>
        </Modal>
      )}
    </>
  );
}